import React from 'react';
import Drawer from '../../common/Drawer';
import SaveCancelButtons from '../../common/buttons/SaveCancelButtons';
import { styled } from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../../data/store';
import { useNavigate } from 'react-router-dom';
import { Controller, useForm } from 'react-hook-form';
import {
	asyncCallDone,
	asyncCallStart,
} from '../../../data/stores/projectStore';
import { showAlert } from '../../../data/stores/alertStore';
import ScreenBusySpinner from '../../common/ScreenBusySpinner';
import { AsyncStage } from '../../../data/reduxCommon';
import { createClient } from '../../../services/client-service';
import { ClientDTO } from '../../../data/models/client';
import { closeModal } from '../../../data/stores/modalStore';
import useClientsList from '../../../hooks/useClientsList';
import Autocomplete from '../../common/form/AutoComplete';
import { Grid } from '@mui/material';

export default function ClientCreationModal() {
	const { openId } = useSelector((state: RootState) => state.modalState);

	const open = openId === 'clientCreationModal';

	const dispatch = useDispatch<AppDispatch>();
	const navigate = useNavigate();
	const { dropdownOptions: clientsDropDownOptions } = useClientsList(open);

	const { stage } = useSelector((state: RootState) => state.clientState);

	const { handleSubmit, control, reset, formState } = useForm({
		defaultValues: {
			name: '',
		},
		mode: 'onSubmit',
	});
	const { errors } = formState;

	const resetForm = () => {
		reset();
	};

	const handleClose = () => {
		dispatch(closeModal());
		resetForm();
	};

	const handleCreateClient = async (data: ClientDTO) => {
		try {
			dispatch(asyncCallStart());
			const createdClient = await createClient(data);
			navigate(`/clients/${createdClient.id}`);
			handleClose();
		} catch (error) {
			console.error('error', error);
			dispatch(
				showAlert({
					message: 'An error occurred creating the client.',
					severity: 'error',
				})
			);
		} finally {
			dispatch(asyncCallDone());
		}
	};

	if (!open) return <></>;

	return (
		<Drawer
			open={open}
			handleClose={handleClose}
			title="Create New Client"
			width={600}
		>
			<ScreenBusySpinner />
			<Container>
				<Grid container spacing={3}>
					<Grid item xs={12}>
						<Controller
							name={'name'}
							control={control}
							rules={{
								required: 'Name is required',
							}}
							render={({ field }) => (
								<Autocomplete
									{...field}
									id={'name'}
									label={'Name'}
									options={clientsDropDownOptions}
									filterOptions={(
										options,
										{ inputValue }
									) => {
										try {
											const match = new RegExp(
												inputValue,
												'i'
											);
											return options
												.filter((option) =>
													match.test(option.display)
												)
												.sort((a, b) => {
													if (
														a.display === inputValue
													)
														return -1;
													if (
														b.display === inputValue
													)
														return 1;
													return 0;
												});
										} catch (e) {
											// If regex fails, do basic string includes match
											return options.filter((option) =>
												option.display
													.toLowerCase()
													.includes(
														inputValue.toLowerCase()
													)
											);
										}
									}}
									onInputChange={(e, value) => {
										const regex = /[^A-Za-z0-9 -]/g;
										if (regex.test(value)) return;
										field.onChange(value);
									}}
									freeSolo
									error={!!errors.name}
									helperText={errors.name?.message}
								/>
							)}
						/>
					</Grid>
				</Grid>

				<ButtonContainer>
					<SaveCancelButtons
						handleSave={handleSubmit(handleCreateClient)}
						handleCancel={handleClose}
						saveDisabled={stage === AsyncStage.Busy}
						cancelDisabled={stage === AsyncStage.Busy}
						saveText={'Create'}
					/>
				</ButtonContainer>
			</Container>
		</Drawer>
	);
}

const Container = styled.div`
	display: flex;
	flex-direction: column;
	height: 100%;
	padding: 16px 0;
`;

const ButtonContainer = styled.div`
	position: absolute;
	bottom: 16px;
	right: 16px;
`;
